import {formatFileSize} from './fileSize';

/**
 * Что можно приложить к сообщению — сторона кабинета (SKS-78, SKS-124).
 *
 * Источник правды — `apps/api/src/common/attachments/attachment-file.ts`: сервер
 * определяет тип по содержимому, а не по имени файла, и отказ всегда за ним.
 * Здесь тот же перечень нужен для другого: чтобы неподдерживаемый файл нельзя
 * было выбрать в проводнике, а отказ был виден сразу, а не после того как человек
 * дописал сообщение и нажал «Отправить». Списки обязаны совпадать — правится
 * один, правится и второй.
 */

/** 50 МБ — как MAX_ATTACHMENT_SIZE на бэкенде. */
export const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;

/** Форматы, которые сервер распознаёт по подписи в первых байтах. */
const SIGNED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'mp4', 'mov'];

/** Документы: их сервер принимает по расширению и отдаёт только на скачивание. */
const DOCUMENT_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'zip'];

const ALLOWED_EXTENSIONS = [...SIGNED_EXTENSIONS, ...DOCUMENT_EXTENSIONS];

/** Типы, которые сервер распознаёт по подписи. SVG среди них нет намеренно. */
const ALLOWED_MIME_TYPES = new Set([
	'image/jpeg',
	'image/png',
	'image/webp',
	'application/pdf',
	'video/mp4',
	'video/quicktime',
]);

/**
 * Значение `accept` для поля выбора файла: и типы, и расширения. Одних типов мало —
 * у doc/xls/zip браузеры проставляют MIME по-разному, а иногда не проставляют вовсе;
 * одних расширений мало — при съёмке на телефон файл приходит из камеры, где имени
 * ещё нет.
 */
export const ATTACHMENT_ACCEPT = [
	...ALLOWED_MIME_TYPES,
	...ALLOWED_EXTENSIONS.map((extension) => `.${extension}`),
].join(',');

/** Текст отказа — дословно как UNSUPPORTED_FILE_MESSAGE на бэкенде. */
export const UNSUPPORTED_FILE_MESSAGE =
	'Такой файл не принимается. Подойдут фото (JPEG, PNG, WebP), видео (MP4, MOV), ' +
	`PDF и документы: ${DOCUMENT_EXTENSIONS.join(', ')}`;

/**
 * Причина отказа словами или null. `accept` — подсказка проводнику, а не запрет:
 * «Все файлы» есть почти всегда, поэтому перечень проверяется и здесь.
 */
export function attachmentProblem(file: File): string | null {
	if (file.size > MAX_ATTACHMENT_SIZE) {
		return `Файл больше ${Math.round(MAX_ATTACHMENT_SIZE / 1024 / 1024)} МБ (${formatFileSize(file.size)})`;
	}

	const dot = file.name.lastIndexOf('.');
	const extension = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : '';

	// Достаточно одного совпадения: у файла из камеры имя без расширения, но тип
	// есть, а у документа наоборот — расширение есть, а тип пустой
	if (ALLOWED_EXTENSIONS.includes(extension) || ALLOWED_MIME_TYPES.has(file.type)) {
		return null;
	}

	return UNSUPPORTED_FILE_MESSAGE;
}
